// @ts-nocheck
/**
 * Catering store gate — EA-3984.
 *
 * Makes the header store selector pick a store that can actually fulfil CATERING, on the
 * catering collection only. Loaded from snippets/catering-fulfillment-gate.liquid, which
 * only the catering template renders; no other page, the PDP included, ever loads it.
 *
 * Why it is needed (measured live 2026-09-26, zip 10011, Next Day, delivery):
 *   - The header's store lookup knows nothing about catering, and for every tier but
 *     1-Hour it auto-selects `stores[0]` in raw response order (EA-3783). That order is
 *     not stable: eight identical requests put 1105 first four times, 1292 three times,
 *     1222 once.
 *   - Store 1292 can deliver 1 of the 107 catering variants. With it saved, every
 *     single-variant catering card opens a "Select size" drawer with one struck
 *     "Default Title" card marked "Pickup only" and a dead Add — the EA-3984 report.
 *     1105, 1222 and 434 (#630) deliver 100–107 of them.
 *   - The saved selection is per browser profile (localStorage), so two browsers that
 *     each ran the lookup can hold different stores — why Chrome failed and Edge did not.
 *
 * What it does:
 *   1. Installs a `storeResultsGate` (the contract in assets/store-availability.js, the
 *      same one the personalization drawer uses) on the header selector. The gate asks v4
 *      — store-less, so it answers for every store in the zip — about a small probe of
 *      the catering products on the page, and narrows the store list to the stores that
 *      can fulfil the catering assortment by the chosen method. The selector's own
 *      auto-select then lands on one of those.
 *   2. When no store can, it refuses the lookup with a message that says so, steering to
 *      the other method when that one works — rather than letting a store be chosen and
 *      every product then read "Pickup only".
 *   3. On page load, re-asks the same question about the SAVED store. A saved store that
 *      fails it re-closes the catering gate and reopens the selector, so a stale selection
 *      (an earlier random pick, or one saved on another page) cannot keep the page broken.
 *
 * Not `hasCatering` / `includeCatering: true`. That flag comes from EA's legacy catering
 * program and does not describe this assortment: at 02108 it marks only store 98, which
 * delivers 0 of the 50 catering products, while 819 and 1388 deliver 48 each; at 60601 it
 * marks 143 (47) but not 243 (49). v4's per-product store lists agree with the
 * store-scoped capabilities the variant drawer and Review & Checkout enforce.
 *
 * Fails OPEN on an unanswerable check — the lookup renders exactly as it did before this
 * file existed. The variant drawer and Review & Checkout stay the real gates.
 */

import { deliverableStores, fetchProductAvailability, isTruthyFlag } from '@theme/ea-availability';

const STORAGE_KEY = 'ea-store-availability';

/**
 * How many catering products the gate asks about.
 *
 * v4 answers in about 3.6 s for 5 ids against ~9 s for the full 50-product assortment
 * (parallel chunks do not beat that; measured 2026-09-26), and the selector waits for the
 * gate before rendering its store list. Five is enough because store capability is
 * bimodal — a store delivers nearly all of the assortment or nearly none. On 2026-09-26
 * the 5-product probe named exactly the stores the full 50-product sweep did at 10011,
 * 10001, 60601, 02108 and 30303.
 */
const PROBE_SIZE = 5;

/**
 * How many more are asked about before a zip is refused outright. Only reached when the
 * probe found NO store, so the extra request is paid by the rare zip that is about to be
 * told "no", never by the common one.
 */
const CONFIRM_SIZE = 20;

const TYPES = ['delivery', 'pickup'];

/**
 * Translated copy from snippets/catering-i18n.liquid; the English `fallback` when the key
 * is missing (EA-4364). Mirrors `i18n` in catering-cart.js. Read off `globalThis` so the
 * pure helpers below still load outside a browser.
 */
function i18n(key, fallback) {
  const value = globalThis.cateringI18n?.[key];
  return typeof value === 'string' && value && !value.startsWith('translation missing') ? value : fallback;
}

const MESSAGES = {
  noDelivery: i18n(
    'store_gate.no_delivery',
    'Catering isn’t available for delivery to this zip code. Choose Pickup instead, or try a different zip code.'
  ),
  noPickup: i18n(
    'store_gate.no_pickup',
    'Catering isn’t available for pickup near this zip code. Choose Delivery instead, or try a different zip code.'
  ),
  none: i18n('store_gate.none', 'Catering isn’t available for this zip code. Please try a different one.'),
  noneNearby: i18n(
    'store_gate.none_nearby',
    'No store near you can fulfil catering orders this way. Try another delivery option or zip code.'
  ),
  staleStore: i18n(
    'store_gate.stale_store',
    'Your saved store can’t fulfil catering orders for this date and method. Choose another store to see what’s available.'
  ),
};

// ── Pure decision helpers (exported for tests) ───────────────────────────────

/**
 * The EA product ids to probe, one per catering card in grid order — the first variant's
 * `externalVariantId` from the card's inventory island (blocks/catering-product-card-atc).
 * The metafield is country-prefixed ('US-18517'); v4 takes the trailing digits.
 * @param {ParentNode} root
 * @param {number} limit
 * @param {number} [skip] cards to pass over first (the confirm probe asks the NEXT ones)
 * @returns {number[]}
 */
export function readCateringProductIds(root, limit, skip = 0) {
  const ids = [];
  const seen = new Set(); // skipped ones included, so a repeated card can't be asked about twice
  for (const card of root.querySelectorAll('[data-catering-atc][data-catering-eligible="true"]')) {
    let inventory = null;
    try {
      inventory = JSON.parse(card.querySelector('[data-catering-inventory]')?.textContent || 'null');
    } catch {
      inventory = null;
    }
    const first = inventory && Object.values(inventory)[0];
    const match = String(first?.externalVariantId ?? '').match(/(\d+)\s*$/);
    const id = match ? Number(match[1]) : NaN;
    if (!Number.isFinite(id) || seen.has(id)) continue;
    seen.add(id);
    if (seen.size <= skip) continue;
    ids.push(id);
    if (ids.length >= limit) break;
  }
  return ids;
}

/**
 * How many of the asked-about products each store can fulfil by `type`, from a store-less
 * v4 answer. Delivery goes through `deliverableStores` (the `isAssigned` picker filter),
 * exactly as the personalization drawer's gate reads it; an entry whose zip-level
 * `available` is false names no store.
 * @param {object} data v4 response
 * @param {'delivery'|'pickup'} type
 * @returns {Map<string, number>}
 */
export function storeCoverage(data, type) {
  const coverage = new Map();
  const entries = data?.[type];
  if (!Array.isArray(entries)) return coverage;
  for (const entry of entries) {
    if (!isTruthyFlag(entry?.available)) continue;
    const stores = type === 'delivery' ? deliverableStores(entry.stores) : entry.stores || [];
    const counted = new Set();
    for (const store of stores) {
      const id = store?.storeId;
      if (id == null || counted.has(String(id))) continue;
      counted.add(String(id));
      coverage.set(String(id), (coverage.get(String(id)) || 0) + 1);
    }
  }
  return coverage;
}

/**
 * The stores that can fulfil the catering assortment: every store covering at least a
 * third of what the best store in the zip covers.
 *
 * Relative to the best store, not to the probe: a zip where every store can make only a
 * few catering products still has those stores to offer, and refusing it would be a lie.
 *
 * A third, not a half, because the probe is five products, and a small sample of a good
 * store can look mediocre. Measured 2026-09-26: at 30303 store 1062 scored 3 of 5 — on a
 * half cut's boundary — yet delivers 45 of the full 50. The stores this exists to keep
 * out score 0 or 1 of 5 (10011's 1292 delivers 1 of 107 variants), and both stay out
 * whenever the best store covers 4 or 5. Every zip measured (10011, 10001, 60601, 02108,
 * 30303) yields the same set under either cut.
 * @param {Map<string, number>} coverage
 * @returns {Set<string>} empty when no store covers anything
 */
export function cateringEligibleStores(coverage) {
  let best = 0;
  for (const count of coverage.values()) best = Math.max(best, count);
  const eligible = new Set();
  if (best === 0) return eligible;
  for (const [id, count] of coverage) if (count > 0 && count * 3 >= best) eligible.add(id);
  return eligible;
}

/**
 * Did v4 actually answer for `type`? An id EA does not know comes back as a 200 with EMPTY
 * `delivery` / `pickup` arrays (measured 2026-09-26), while a real refusal carries an entry
 * per product with `available: false`. Only the second is a verdict — reading the first
 * as "no store can" would refuse every zip the moment the catalogue's ids went stale.
 * @param {object} data v4 response
 * @param {string} type
 */
function answered(data, type) {
  return Array.isArray(data?.[type]) && data[type].length > 0;
}

/**
 * The gate verdict for one fulfillment type, in the shape `storeResultsGate.resolve`
 * returns. No answer — `data` null (nothing asked, or the check failed) or no entries for
 * `type` (see `answered`) — is `{ ok: true }`: fail open.
 * @param {object|null} data v4 response
 * @param {string} type the method the shopper is on
 * @returns {{ok: boolean, message?: string, eligibleStoreIds?: Set<string>, ifNoneNearby?: object}}
 */
export function verdictFor(data, type) {
  if (!TYPES.includes(type) || !answered(data, type)) return { ok: true };
  const eligible = cateringEligibleStores(storeCoverage(data, type));
  if (eligible.size > 0) {
    return { ok: true, eligibleStoreIds: eligible, ifNoneNearby: { message: MESSAGES.noneNearby } };
  }
  const other = type === 'delivery' ? 'pickup' : 'delivery';
  if (answered(data, other) && cateringEligibleStores(storeCoverage(data, other)).size > 0) {
    return { ok: false, message: type === 'delivery' ? MESSAGES.noDelivery : MESSAGES.noPickup };
  }
  return { ok: false, message: MESSAGES.none };
}

// ── The gate ─────────────────────────────────────────────────────────────────

/**
 * Build the gate object. Dependencies are injected so the tests drive the real logic.
 * @param {{ root: ParentNode, fetchAvailability: (params: object) => Promise<object> }} deps
 */
export function createCateringStoreGate({ root, fetchAvailability }) {
  /** `country|zip|date` -> Promise<v4 body|null>. v4 answers every type in one response. */
  const probes = new Map();

  const ask = (ctx, ids) =>
    fetchAvailability({ productIds: ids, date: ctx.fulfillmentDate, zipCode: ctx.zipCode, country: ctx.country || 'US' }).catch((error) => {
      console.warn('[catering-store-gate] availability check failed; store list left unfiltered:', error);
      return null;
    });

  const probe = (ctx) => {
    if (!ctx?.zipCode || !ctx?.fulfillmentDate) return null;
    const ids = readCateringProductIds(root, PROBE_SIZE);
    if (ids.length === 0) return null;
    const key = `${String(ctx.country || 'US').toUpperCase()}|${ctx.zipCode}|${ctx.fulfillmentDate}`;
    if (!probes.has(key)) probes.set(key, ask(ctx, ids));
    return probes.get(key);
  };

  /**
   * The verdict for `ctx`. A "no" from the probe is confirmed against the next
   * CONFIRM_SIZE products before it is returned, so a zip is never refused on five
   * products alone.
   */
  const decide = async (ctx, type) => {
    const pending = probe(ctx);
    if (!pending) return { ok: true };
    const data = await pending;
    const verdict = verdictFor(data, type);
    if (verdict.ok) return verdict;

    const more = readCateringProductIds(root, CONFIRM_SIZE, PROBE_SIZE);
    if (more.length === 0) return verdict;
    const wider = await ask(ctx, more);
    if (!wider) return { ok: true }; // could not confirm the "no": fail open
    const confirmed = verdictFor(wider, type);
    // The wider set found capable stores the probe missed: offer those.
    return confirmed.ok ? confirmed : verdict;
  };

  return {
    onLookupStart(ctx) {
      probe(ctx);
    },
    async resolve(ctx) {
      // Shipment-only areas are refused by the selector itself on this page
      // (data-local-fulfillment-only); there is no store list to narrow.
      if (ctx?.shipmentOnly) return { ok: true };
      return decide(ctx, ctx?.requestedFulfillmentType || ctx?.fulfillmentType);
    },
    /**
     * Is a SAVED selection's store one the gate would have offered? Resolves true when
     * there is nothing to judge or the check cannot answer (fail open).
     * @param {object} selection the `ea-store-availability` record
     */
    async acceptsSaved(selection) {
      const type = String(selection?.fulfillmentType || '').toLowerCase();
      const storeId = selection?.storeId;
      if (!TYPES.includes(type) || storeId == null || storeId === '') return true;
      const ctx = {
        zipCode: selection.zipCode,
        fulfillmentDate: selection.deliveryDate,
        country: selection.country || 'US',
      };
      const verdict = await decide(ctx, type);
      if (verdict.ok && !(verdict.eligibleStoreIds instanceof Set)) return true; // nothing asked / fail open
      return verdict.ok && verdict.eligibleStoreIds.has(String(storeId));
    },
  };
}

// ── Wiring (browser only) ────────────────────────────────────────────────────

function readSelection() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : null;
    return state && typeof state === 'object' ? state : null;
  } catch {
    return null;
  }
}

/** The same selection, field for field, on the four inputs the verdict depends on. */
function sameSelection(a, b) {
  return (
    !!a &&
    !!b &&
    String(a.storeId) === String(b.storeId) &&
    a.zipCode === b.zipCode &&
    a.deliveryDate === b.deliveryDate &&
    a.fulfillmentType === b.fulfillmentType
  );
}

async function install() {
  // The header selector, never the drawers' embedded copies (they carry their own gates).
  const selector = document.querySelector('store-availability-component:not([data-embedded="true"])');
  if (!selector) return;

  // Assigned only once the element is upgraded: the class declares `storeResultsGate = null`
  // as a field, which would silently overwrite a property set on the element before then.
  await customElements.whenDefined('store-availability-component');
  if (selector.storeResultsGate) return; // someone else owns it — never displace a gate

  const gate = createCateringStoreGate({ root: document, fetchAvailability: fetchProductAvailability });
  selector.storeResultsGate = gate;

  // A saved selection the gate would not have offered: close the catering gate on it and
  // reopen the selector, whose next lookup runs through the gate and auto-selects a store
  // that can fulfil catering. Skipped when nothing fulfillable is saved — the catering gate
  // is already closed and asking for a store.
  const saved = readSelection();
  const fulfillment = window.EACateringFulfillment;
  if (!saved || !fulfillment || !fulfillment.isSelectionFulfillable(saved)) return;
  if (await gate.acceptsSaved(saved)) return;
  rejectStaleSelection(saved, {
    readSelection,
    fulfillment,
    cartIsEmpty: () => !window.EACateringCart || window.EACateringCart.isEmpty(),
    onCartEmptied: (callback) => {
      const listener = () => {
        if (window.EACateringCart && !window.EACateringCart.isEmpty()) return;
        document.removeEventListener('catering:cart:updated', listener);
        callback();
      };
      document.addEventListener('catering:cart:updated', listener);
    },
    openSelector: () => {
      if (typeof window.openStoreAvailability === 'function') window.openStoreAvailability();
    },
  });
}

/**
 * Act on a saved store the gate would not have offered.
 *
 * A non-empty catering cart HOLDS its store (EA-3984): its items were checked against it,
 * the store lock refuses to swap it without clearing the cart, and Review & Checkout judges
 * the cart against it. The verdict can land after the shopper has already added something
 * — it waits on a v4 call — so rejecting then would either strand a cart that store can
 * fulfil (checkout refused) or invite a replacement store under it. So the rejection waits
 * until the cart is empty, and is applied then without reopening the selector unasked.
 *
 * @param {object} saved the selection the verdict was reached for
 * @param {{readSelection: () => object|null, fulfillment: {rejectSelection: Function},
 *   cartIsEmpty: () => boolean, onCartEmptied: (cb: () => void) => void,
 *   openSelector: () => void}} deps
 * @returns {'rejected'|'deferred'|'superseded'}
 */
export function rejectStaleSelection(saved, { readSelection, fulfillment, cartIsEmpty, onCartEmptied, openSelector }) {
  // The shopper may have saved a new selection while the check was running.
  if (!sameSelection(saved, readSelection())) return 'superseded';

  if (!cartIsEmpty()) {
    onCartEmptied(() => {
      if (sameSelection(saved, readSelection())) fulfillment.rejectSelection(saved, MESSAGES.staleStore);
    });
    return 'deferred';
  }

  fulfillment.rejectSelection(saved, MESSAGES.staleStore);
  openSelector();
  return 'rejected';
}

if (typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => install());
  } else {
    install();
  }
}
